import type { MatchSorterOptions } from 'match-sorter';
import type { DirectoryProvider } from '#services/api/provider.ts';

// Search is `matchSorter`, as every other picker in insights does it — the portal and `apps/app`
// copies hand-roll a regex over the fields concatenated into one string.
/** The fields a coordinator might remember a provider by, all searched at once. */
export const searchKeys: MatchSorterOptions<DirectoryProvider>['keys'] = [
  'name',
  'credential',
  'specialty',
  'email',
  'mobile',
  // The API formats mobiles ("(555) 018-8801"), so the number as anyone types it from memory matches
  // nothing without a digits-only view of the same field alongside the formatted one.
  (provider) => (provider.mobile ?? '').replace(/\D/g, ''),
  // Searchable because ids are what a shared link, a support ticket, or a log line carries.
  (provider) => String(provider.id),
];

/**
 * Equal-ranking matches keep the order the directory arrived in: last name, with Unassigned first.
 * Without this, match-sorter's default tie-break re-sorts by the matched value — which for an empty
 * search is every option, alphabetically by a given-name-first label, so the list a user sees would
 * disagree with the ordering the API was asked for.
 */
export const keepDirectoryOrder: MatchSorterOptions<DirectoryProvider>['baseSort'] = (a, b) =>
  a.index - b.index;

/**
 * Selected providers first, each group otherwise in the order it was given.
 *
 * The field shows one chip and an overflow count, so the dropdown carries the burden of stating the
 * whole scope — without this, "who am I scoped to?" means scrolling an alphabetical list of hundreds
 * hunting for highlighted rows.
 */
export function selectedFirst(
  providers: DirectoryProvider[],
  selectedIds: ReadonlySet<DirectoryProvider['id']>
): DirectoryProvider[] {
  if (selectedIds.size === 0) return providers;

  return [
    ...providers.filter((provider) => selectedIds.has(provider.id)),
    ...providers.filter((provider) => !selectedIds.has(provider.id)),
  ];
}
